import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ChevronRight, PlayCircle, Lightbulb, CheckCircle, Clock, Brain, BarChart3, Bell, Settings, Eye, RefreshCw, Sparkles, Bot, BookOpen, Info, Home, ArrowRight } from 'lucide-react';
import StaticPageLayout from '../../../components/StaticPageLayout';
import { useThemeStore } from '../../../store/theme';

const tableOfContents = [
  { id: 'overview', title: 'Overview' },
  { id: 'key-features', title: 'Key Features' },
  { id: 'video-tutorial', title: 'Video Tutorial' },
  { id: 'getting-started', title: 'Getting Started' },
  { id: 'insights-dashboard', title: 'Insights Dashboard' },
  { id: 'notifications', title: 'Smart Notifications' },
  { id: 'customization', title: 'Customization' },
  { id: 'best-practices', title: 'Best Practices' },
];

const relatedArticles = [
  { title: 'Getting Started with SweetPilot', href: '/doc/article/getting-started' },
  { title: 'Onboarding Tutorial', href: '/doc/article/onboarding-tutorial' },
  { title: 'AI Models in SweetPilot', href: '/doc/article/ai-models' },
  { title: 'Multi-Language Support', href: '/doc/article/multi-language-support' },
  { title: 'What is SweetPilot?', href: '/doc/article/what-is-sweetpilot' },
];

const keyFeatures = [
  {
    icon: Eye,
    title: 'Real-Time Insights',
    description: 'Get instant AI-powered insights across all your connected integrations',
    duration: '2 min'
  },
  {
    icon: BarChart3,
    title: 'Predictive Analytics',
    description: 'Leverage machine learning for forecasting and trend analysis',
    duration: '3 min'
  },
  {
    icon: Bell,
    title: 'Smart Notifications',
    description: 'Receive intelligent alerts based on your business priorities',
    duration: '2 min'
  },
  {
    icon: RefreshCw,
    title: 'Cross-Platform Analysis',
    description: 'Unified intelligence from all your connected services',
    duration: '3 min'
  },
  {
    icon: Sparkles,
    title: 'AI Recommendations',
    description: 'Get actionable suggestions to optimize your workflows',
    duration: '3 min'
  },
  {
    icon: Settings,
    title: 'Custom Dashboards',
    description: 'Build personalized views tailored to your needs',
    duration: '2 min'
  }
];

interface TipBoxProps {
  type: 'tip' | 'warning' | 'info';
  title: string;
  children: React.ReactNode;
}

const TipBox = ({ type, title, children }: TipBoxProps) => {
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';

  const styles = {
    tip: {
      bg: isDark ? 'bg-green-900/30' : 'bg-green-50',
      border: 'border-green-500',
      icon: <Lightbulb className="w-5 h-5 text-green-500" />,
      titleColor: isDark ? 'text-green-400' : 'text-green-700'
    },
    warning: {
      bg: isDark ? 'bg-yellow-900/30' : 'bg-yellow-50',
      border: 'border-yellow-500',
      icon: <Info className="w-5 h-5 text-yellow-500" />,
      titleColor: isDark ? 'text-yellow-400' : 'text-yellow-700'
    },
    info: {
      bg: isDark ? 'bg-blue-900/30' : 'bg-blue-50',
      border: 'border-blue-500',
      icon: <Info className="w-5 h-5 text-blue-500" />,
      titleColor: isDark ? 'text-blue-400' : 'text-blue-700'
    }
  };

  const style = styles[type];

  return (
    <div className={`${style.bg} border-l-4 ${style.border} p-4 rounded-r-lg my-4`}>
      <div className="flex items-center gap-2 mb-2">
        {style.icon}
        <span className={`font-semibold ${style.titleColor}`}>{title}</span>
      </div>
      <div className={isDark ? 'text-gray-300' : 'text-gray-700'}>
        {children}
      </div>
    </div>
  );
};

const BrainAITutorial = () => {
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';
  const [activeSection, setActiveSection] = useState('');

  const schemaMarkup = {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    name: 'SweetPilot Brain AI - Complete Video Tutorial',
    description: 'Comprehensive video tutorial on using SweetPilot Brain AI for intelligent insights, real-time analytics, and AI-powered recommendations across all your connected integrations.',
    thumbnailUrl: 'https://sweetpilot.com/thumbnails/brain-ai-tutorial.jpg',
    uploadDate: '2024-01-15',
    duration: 'PT15M',
    contentUrl: 'https://sweetpilot.com/videos/brain-ai-tutorial.mp4',
    embedUrl: 'https://sweetpilot.com/embed/brain-ai-tutorial',
    publisher: {
      '@type': 'Organization',
      name: 'SweetPilot',
      logo: {
        '@type': 'ImageObject',
        url: 'https://sweetpilot.com/logo.png'
      }
    },
    author: {
      '@type': 'Organization',
      name: 'SweetPilot Team'
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': 'https://sweetpilot.com/doc/article/brain-ai-tutorial'
    },
    datePublished: '2024-01-15',
    dateModified: '2024-12-01'
  };

  useEffect(() => {
    const handleScroll = () => {
      const sections = tableOfContents.map(item => document.getElementById(item.id));
      const scrollPosition = window.scrollY + 100;

      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i];
        if (section && section.offsetTop <= scrollPosition) {
          setActiveSection(tableOfContents[i].id);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <StaticPageLayout
        title=""
        subtitle=""
        showBackButton={false}
      >
      <Helmet>
        <title>SweetPilot Brain AI Tutorial - AI-Powered Insights | SweetPilot Help Center</title>
        <meta name="description" content="Learn how to use SweetPilot Brain AI for intelligent insights, real-time analytics, and AI-powered recommendations. Complete video tutorial with step-by-step guidance." />
        <meta name="keywords" content="SweetPilot Brain AI, AI insights, artificial intelligence, analytics tutorial, AI recommendations, smart notifications, predictive analytics, SweetPilot tutorial" />
        <meta property="og:title" content="SweetPilot Brain AI - Complete Video Tutorial" />
        <meta property="og:description" content="Master SweetPilot Brain AI with our comprehensive video tutorial. Learn to leverage AI-powered insights and analytics for your business." />
        <meta property="og:type" content="video.other" />
        <meta property="og:url" content="https://sweetpilot.com/doc/article/brain-ai-tutorial" />
        <meta property="og:image" content="https://sweetpilot.com/thumbnails/brain-ai-tutorial.jpg" />
        <meta property="og:video" content="https://sweetpilot.com/videos/brain-ai-tutorial.mp4" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="SweetPilot Brain AI Tutorial - AI-Powered Insights" />
        <meta name="twitter:description" content="Complete video tutorial on using SweetPilot Brain AI for intelligent insights and analytics." />
        <meta name="twitter:image" content="https://sweetpilot.com/thumbnails/brain-ai-tutorial.jpg" />
        <link rel="canonical" href="https://sweetpilot.com/doc/article/brain-ai-tutorial" />
        <script type="application/ld+json">
          {JSON.stringify(schemaMarkup)}
        </script>
      </Helmet>

      <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
        {/* Hero Section */}
        <div className={`${isDark ? 'bg-gradient-to-r from-purple-900 via-indigo-900 to-purple-900' : 'bg-gradient-to-r from-purple-600 via-indigo-600 to-purple-600'} text-white py-16`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Breadcrumb */}
            <nav className="flex items-center space-x-2 text-sm mb-6 text-purple-200">
              <Link to="/" className="hover:text-white flex items-center gap-1">
                <Home className="w-4 h-4" /> Home
              </Link>
              <ChevronRight className="w-4 h-4" />
              <Link to="/help" className="hover:text-white">Help Center</Link>
              <ChevronRight className="w-4 h-4" />
              <span className="text-white">Brain AI Tutorial</span>
            </nav>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="flex items-center gap-3 mb-4">
                <Brain className="w-10 h-10 text-purple-300" />
                <span className="px-3 py-1 bg-purple-500/30 rounded-full text-sm">Video Tutorial</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-4">
                SweetPilot Brain AI Tutorial
              </h1>
              <p className="text-xl text-purple-200 max-w-3xl">
                Unlock the power of AI-driven insights. Learn how Brain AI analyzes your data across all integrations to provide intelligent recommendations and real-time analytics.
              </p>
              <div className="flex items-center gap-4 mt-6 text-purple-200">
                <span className="flex items-center gap-2">
                  <Clock className="w-4 h-4" /> 15 min video
                </span>
                <span className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" /> 6 key features
                </span>
              </div>
            </motion.div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Sidebar - Table of Contents */}
            <div className="lg:col-span-1">
              <div className={`sticky top-24 ${isDark ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-lg p-6`}>
                <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Table of Contents
                </h3>
                <nav className="space-y-2">
                  {tableOfContents.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => scrollToSection(item.id)}
                      className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left text-sm transition-colors ${
                        activeSection === item.id
                          ? isDark
                            ? 'bg-purple-600 text-white'
                            : 'bg-purple-100 text-purple-700'
                          : isDark
                          ? 'text-gray-400 hover:bg-gray-700 hover:text-white'
                          : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                      }`}
                    >
                      <ChevronRight className="w-3 h-3" />
                      {item.title}
                    </button>
                  ))}
                </nav>
              </div>
            </div>

            {/* Main Content */}
            <div className="lg:col-span-3">
              <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-lg p-8`}>
                {/* Overview Section */}
                <section id="overview" className="mb-12">
                  <h2 className={`text-2xl font-bold mb-4 flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    <Brain className="w-6 h-6 text-purple-500" /> Overview
                  </h2>
                  <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                    SweetPilot Brain AI is your intelligent command center that transforms raw data from all your connected integrations into actionable insights. Using advanced machine learning algorithms, Brain AI analyzes patterns, predicts trends, and provides personalized recommendations to help you make smarter decisions.
                  </p>
                  <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                    Whether you're managing projects in Trello, tracking time in Clockify, or handling customer support in Zendesk, Brain AI connects the dots and reveals insights you might have missed.
                  </p>

                  <TipBox type="info" title="What You'll Learn">
                    This tutorial covers everything from accessing the Brain AI dashboard to customizing your insights and setting up smart notifications. By the end, you'll be leveraging AI to optimize your workflows.
                  </TipBox>
                </section>

                {/* Key Features Section */}
                <section id="key-features" className="mb-12">
                  <h2 className={`text-2xl font-bold mb-6 flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    <Sparkles className="w-6 h-6 text-purple-500" /> Key Features
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {keyFeatures.map((feature, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className={`p-4 rounded-lg border ${
                          isDark
                            ? 'bg-gray-700/50 border-gray-600'
                            : 'bg-gray-50 border-gray-200'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`p-2 rounded-lg ${isDark ? 'bg-purple-600' : 'bg-purple-100'}`}>
                            <feature.icon className={`w-5 h-5 ${isDark ? 'text-white' : 'text-purple-600'}`} />
                          </div>
                          <div className="flex-1">
                            <h3 className={`font-semibold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                              {feature.title}
                            </h3>
                            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                              {feature.description}
                            </p>
                            <span className={`inline-flex items-center gap-1 text-xs mt-2 ${isDark ? 'text-purple-400' : 'text-purple-600'}`}>
                              <Clock className="w-3 h-3" /> {feature.duration}
                            </span>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </section>

                {/* Video Tutorial Section */}
                <section id="video-tutorial" className="mb-12">
                  <h2 className={`text-2xl font-bold mb-4 flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    <PlayCircle className="w-6 h-6 text-purple-500" /> Video Tutorial
                  </h2>
                  <div className={`aspect-video rounded-xl overflow-hidden ${isDark ? 'bg-gray-700' : 'bg-gray-200'} flex items-center justify-center`}>
                    <div className="text-center">
                      <div className={`w-20 h-20 rounded-full ${isDark ? 'bg-purple-600' : 'bg-purple-500'} flex items-center justify-center mx-auto mb-4 cursor-pointer hover:scale-110 transition-transform`}>
                        <PlayCircle className="w-10 h-10 text-white" />
                      </div>
                      <p className={`${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        Click to play the Brain AI tutorial
                      </p>
                      <p className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'} mt-2`}>
                        Duration: 15 minutes
                      </p>
                    </div>
                  </div>

                  <TipBox type="tip" title="Pro Tip">
                    Use the chapter markers in the video player to jump directly to specific features. You can also adjust playback speed for a quicker overview.
                  </TipBox>
                </section>

                {/* Getting Started Section */}
                <section id="getting-started" className="mb-12">
                  <h2 className={`text-2xl font-bold mb-4 flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    <Bot className="w-6 h-6 text-purple-500" /> Getting Started with Brain AI
                  </h2>
                  <div className="space-y-4">
                    <div className={`p-4 rounded-lg border-l-4 border-purple-500 ${isDark ? 'bg-gray-700/50' : 'bg-purple-50'}`}>
                      <h3 className={`font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        Step 1: Access Brain AI
                      </h3>
                      <p className={isDark ? 'text-gray-300' : 'text-gray-600'}>
                        Navigate to the Brain AI section from your main dashboard. Look for the brain icon in the sidebar navigation or click on "Brain AI" in the top menu.
                      </p>
                    </div>
                    <div className={`p-4 rounded-lg border-l-4 border-purple-500 ${isDark ? 'bg-gray-700/50' : 'bg-purple-50'}`}>
                      <h3 className={`font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        Step 2: Connect Your Integrations
                      </h3>
                      <p className={isDark ? 'text-gray-300' : 'text-gray-600'}>
                        Brain AI works best with multiple data sources. Ensure you have at least one integration connected. The more integrations you connect, the more comprehensive your insights will be.
                      </p>
                    </div>
                    <div className={`p-4 rounded-lg border-l-4 border-purple-500 ${isDark ? 'bg-gray-700/50' : 'bg-purple-50'}`}>
                      <h3 className={`font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        Step 3: Initial Analysis
                      </h3>
                      <p className={isDark ? 'text-gray-300' : 'text-gray-600'}>
                        Brain AI will automatically begin analyzing your data. The initial analysis may take a few minutes depending on the amount of historical data. You'll see a progress indicator during this process.
                      </p>
                    </div>
                    <div className={`p-4 rounded-lg border-l-4 border-purple-500 ${isDark ? 'bg-gray-700/50' : 'bg-purple-50'}`}>
                      <h3 className={`font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        Step 4: Explore Your Insights
                      </h3>
                      <p className={isDark ? 'text-gray-300' : 'text-gray-600'}>
                        Once analysis is complete, explore the various insight cards on your dashboard. Each card represents a different type of analysis or recommendation.
                      </p>
                    </div>
                  </div>
                </section>

                {/* Insights Dashboard Section */}
                <section id="insights-dashboard" className="mb-12">
                  <h2 className={`text-2xl font-bold mb-4 flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    <BarChart3 className="w-6 h-6 text-purple-500" /> Understanding the Insights Dashboard
                  </h2>
                  <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                    The Brain AI dashboard is organized into several key areas:
                  </p>
                  <ul className={`list-disc pl-6 space-y-2 mb-4 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                    <li><strong>Overview Cards:</strong> High-level metrics and KPIs across all integrations</li>
                    <li><strong>Trend Analysis:</strong> Visual charts showing patterns over time</li>
                    <li><strong>Recommendations:</strong> AI-generated suggestions for improvement</li>
                    <li><strong>Alerts Panel:</strong> Important items requiring your attention</li>
                    <li><strong>Integration Status:</strong> Health check of all connected services</li>
                  </ul>

                  <TipBox type="info" title="Dashboard Customization">
                    You can rearrange dashboard cards by dragging and dropping them. Click the gear icon on any card to configure what data it displays.
                  </TipBox>
                </section>

                {/* Smart Notifications Section */}
                <section id="notifications" className="mb-12">
                  <h2 className={`text-2xl font-bold mb-4 flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    <Bell className="w-6 h-6 text-purple-500" /> Smart Notifications
                  </h2>
                  <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                    Brain AI's smart notification system keeps you informed without overwhelming you. The AI learns your preferences and priorities to deliver only the most relevant alerts.
                  </p>
                  <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-700/50' : 'bg-gray-100'} mb-4`}>
                    <h4 className={`font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      Notification Types:
                    </h4>
                    <ul className={`space-y-2 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500" /> Performance milestones and achievements
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-yellow-500" /> Anomaly detection and warnings
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-blue-500" /> Optimization opportunities
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-purple-500" /> Trend predictions and forecasts
                      </li>
                    </ul>
                  </div>

                  <TipBox type="warning" title="Notification Frequency">
                    If you're receiving too many notifications, adjust your sensitivity settings in Brain AI preferences. You can also set quiet hours during which non-critical alerts are held.
                  </TipBox>
                </section>

                {/* Customization Section */}
                <section id="customization" className="mb-12">
                  <h2 className={`text-2xl font-bold mb-4 flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    <Settings className="w-6 h-6 text-purple-500" /> Customization Options
                  </h2>
                  <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                    Tailor Brain AI to match your specific needs and workflow preferences:
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-700/50' : 'bg-gray-100'}`}>
                      <h4 className={`font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        Data Sources
                      </h4>
                      <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        Choose which integrations to include in your analysis
                      </p>
                    </div>
                    <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-700/50' : 'bg-gray-100'}`}>
                      <h4 className={`font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        Analysis Frequency
                      </h4>
                      <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        Set how often Brain AI refreshes its insights
                      </p>
                    </div>
                    <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-700/50' : 'bg-gray-100'}`}>
                      <h4 className={`font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        Focus Areas
                      </h4>
                      <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        Prioritize specific metrics or business goals
                      </p>
                    </div>
                    <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-700/50' : 'bg-gray-100'}`}>
                      <h4 className={`font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        Report Scheduling
                      </h4>
                      <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        Schedule automatic insight reports via email
                      </p>
                    </div>
                  </div>
                </section>

                {/* Best Practices Section */}
                <section id="best-practices" className="mb-12">
                  <h2 className={`text-2xl font-bold mb-4 flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    <Lightbulb className="w-6 h-6 text-purple-500" /> Best Practices
                  </h2>
                  <div className="space-y-4">
                    <div className={`flex items-start gap-3 p-4 rounded-lg ${isDark ? 'bg-gray-700/50' : 'bg-gray-100'}`}>
                      <span className="flex-shrink-0 w-8 h-8 rounded-full bg-purple-500 text-white flex items-center justify-center font-bold">1</span>
                      <div>
                        <h4 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Connect Multiple Integrations</h4>
                        <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                          Brain AI's power comes from cross-platform analysis. The more data sources, the better the insights.
                        </p>
                      </div>
                    </div>
                    <div className={`flex items-start gap-3 p-4 rounded-lg ${isDark ? 'bg-gray-700/50' : 'bg-gray-100'}`}>
                      <span className="flex-shrink-0 w-8 h-8 rounded-full bg-purple-500 text-white flex items-center justify-center font-bold">2</span>
                      <div>
                        <h4 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Review Insights Regularly</h4>
                        <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                          Make it a habit to check your Brain AI dashboard daily or weekly to catch trends early.
                        </p>
                      </div>
                    </div>
                    <div className={`flex items-start gap-3 p-4 rounded-lg ${isDark ? 'bg-gray-700/50' : 'bg-gray-100'}`}>
                      <span className="flex-shrink-0 w-8 h-8 rounded-full bg-purple-500 text-white flex items-center justify-center font-bold">3</span>
                      <div>
                        <h4 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Act on Recommendations</h4>
                        <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                          Don't just view insights—implement the recommendations. Brain AI learns from your actions to improve future suggestions.
                        </p>
                      </div>
                    </div>
                    <div className={`flex items-start gap-3 p-4 rounded-lg ${isDark ? 'bg-gray-700/50' : 'bg-gray-100'}`}>
                      <span className="flex-shrink-0 w-8 h-8 rounded-full bg-purple-500 text-white flex items-center justify-center font-bold">4</span>
                      <div>
                        <h4 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Provide Feedback</h4>
                        <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                          Use the thumbs up/down on insights to help Brain AI learn what's valuable to you.
                        </p>
                      </div>
                    </div>
                  </div>
                </section>

                {/* Related Articles */}
                <section className={`border-t pt-8 mt-8 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                  <h3 className={`text-xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    Related Articles
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {relatedArticles.map((article, index) => (
                      <Link
                        key={index}
                        to={article.href}
                        className={`flex items-center gap-3 p-4 rounded-lg border transition-colors ${
                          isDark
                            ? 'border-gray-700 hover:bg-gray-700'
                            : 'border-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        <BookOpen className={`w-5 h-5 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
                        <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>{article.title}</span>
                        <ArrowRight className={`w-4 h-4 ml-auto ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                      </Link>
                    ))}
                  </div>
                </section>

                {/* Feedback Section */}
                <section className={`mt-8 p-6 rounded-xl ${isDark ? 'bg-gray-700/50' : 'bg-gray-100'}`}>
                  <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    Was this tutorial helpful?
                  </h3>
                  <p className={`mb-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    Help us improve by sharing your feedback
                  </p>
                  <div className="flex gap-4">
                    <button className={`px-6 py-2 rounded-lg transition-colors ${
                      isDark
                        ? 'bg-green-600 hover:bg-green-700 text-white'
                        : 'bg-green-500 hover:bg-green-600 text-white'
                    }`}>
                      Yes, helpful
                    </button>
                    <button className={`px-6 py-2 rounded-lg transition-colors ${
                      isDark
                        ? 'bg-gray-600 hover:bg-gray-500 text-white'
                        : 'bg-gray-300 hover:bg-gray-400 text-gray-700'
                    }`}>
                      Needs improvement
                    </button>
                  </div>
                </section>
              </div>
            </div>
          </div>
        </div>
      </div>
    </StaticPageLayout>
  );
};

export default BrainAITutorial;
